import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import AutoComplete from '../components/AutoComplete';
import Confirm from '../components/Confirm';
import Toast from '../components/Toast';
import ImprimirParteModal from '../components/ImprimirParteModal';
import { useAuth } from '../context/AuthContext';
import { parteSalidaService, trabajadorService } from '../services/api';
import { getErrorDetail } from '../utils/errorHelper';
import '../styles/parte_entrada_list.css';
import '../styles/articulos.css';

const fmtFecha = (d) => (d ? d.split('T')[0] : '—');
const today    = () => new Date().toISOString().split('T')[0];
const weekAgo  = () => { const d = new Date(); d.setDate(d.getDate() - 7); return d.toISOString().split('T')[0]; };

const ESTADO_CFG = {
  'GENERADO':       { color: '#60a5fa', bg: 'rgba(96,165,250,0.12)',  border: 'rgba(96,165,250,0.3)'  },
  'APLICADO PAR.':  { color: '#fb923c', bg: 'rgba(251,146,60,0.12)',  border: 'rgba(251,146,60,0.3)'  },
  'APLICADO TOTAL': { color: '#22c55e', bg: 'rgba(34,197,94,0.12)',   border: 'rgba(34,197,94,0.3)'   },
};
const ESTADOS = ['GENERADO', 'APLICADO PAR.', 'APLICADO TOTAL'];

const PAGE_SIZES = [10, 25, 50, 100];

const EMPTY_FILTERS = {
  sucursal:       '',
  almacen:        '',
  proveedor:      '',
  tipoMovimiento: '',
  docRef1:        '',
  numRef1:        '',
  estado:         '',
  fechaInicio:    weekAgo(),
  fechaFin:       today(),
  sinFecha:       false,
};

export default function ParteSalidaList() {
  const navigate = useNavigate();
  const { sucursal: sesionSucursal } = useAuth();

  const [partes,   setPartes]   = useState([]);
  const [loading,  setLoading]  = useState(true);
  // Por defecto filtra por la sucursal del usuario logueado
  const [filters,  setFilters]  = useState({ ...EMPTY_FILTERS, sucursal: sesionSucursal?.nombre || '' });
  const [pageSize, setPageSize] = useState(25);
  const [page,     setPage]     = useState(1);
  const [toast,    setToast]    = useState(null);
  const [confirm,  setConfirm]  = useState(null);
  const [imprimirTarget, setImprimirTarget] = useState(null);
  const [trabajadores,   setTrabajadores]   = useState([]);
  const [verTarget,       setVerTarget]       = useState(null);
  const [verData,         setVerData]         = useState(null);
  const [verLoading,      setVerLoading]      = useState(false);
  const [editarTarget,    setEditarTarget]    = useState(null);
  const [editarForm,      setEditarForm]      = useState(null);
  const [editarLoading,   setEditarLoading]   = useState(false);
  const [editarSaving,    setEditarSaving]    = useState(false);

  const setF = (key, val) => { setFilters(prev => ({ ...prev, [key]: val })); setPage(1); };

  const fetchPartes = useCallback(async () => {
    setLoading(true);
    try {
      const r = await parteSalidaService.getAll();
      setPartes(r.data || []);
      setPage(1);
    } catch {
      setToast({ type: 'error', message: 'Error al cargar los partes de salida.' });
    } finally { setLoading(false); }
  }, []);

  // Cargar trabajadores una vez (necesarios para las firmas del modal de impresión)
  useEffect(() => {
    trabajadorService.getAll()
      .then(r => setTrabajadores(r.data || []))
      .catch(() => { /* silencioso: si falla el modal solo mostrará lista vacía */ });
  }, []);

  useEffect(() => { fetchPartes(); }, [fetchPartes]);

  /* ── Opciones para autocompletes ── */
  const opsSucursal = useMemo(() => {
    const seen = new Set();
    return partes
      .filter(p => p.sucursal_nombre && !seen.has(p.sucursal_nombre) && seen.add(p.sucursal_nombre))
      .map(p => ({ id: p.sucursal_nombre, nombre: p.sucursal_nombre }));
  }, [partes]);

  const opsAlmacen = useMemo(() => {
    const seen = new Set();
    return partes
      .filter(p => p.almacen_nombre && !seen.has(p.almacen_nombre) && seen.add(p.almacen_nombre))
      .map(p => ({ id: p.almacen_nombre, nombre: p.almacen_nombre }));
  }, [partes]);

  const opsProveedor = useMemo(() => {
    const seen = new Set();
    return partes
      .filter(p => p.proveedor_nombre && !seen.has(p.proveedor_nombre) && seen.add(p.proveedor_nombre))
      .map(p => ({ id: p.proveedor_nombre, nombre: p.proveedor_nombre, ruc: p.ruc_proveedor || '' }));
  }, [partes]);

  const opsDocRef1 = useMemo(() => {
    const seen = new Set();
    return partes
      .filter(p => p.tipo_doc_ref1_descripcion && !seen.has(p.tipo_doc_ref1_descripcion) && seen.add(p.tipo_doc_ref1_descripcion))
      .map(p => ({ id: p.tipo_doc_ref1_descripcion, nombre: p.tipo_doc_ref1_descripcion }));
  }, [partes]);

  const opsTipoMov = useMemo(() => {
    const seen = new Set();
    return partes
      .filter(p => p.tipo_movimiento_descripcion && !seen.has(p.tipo_movimiento_descripcion) && seen.add(p.tipo_movimiento_descripcion))
      .map(p => ({ id: p.tipo_movimiento_descripcion, nombre: p.tipo_movimiento_descripcion }));
  }, [partes]);

  /* ── Filtrado ── */
  const filtered = useMemo(() => {
    return partes.filter(p => {
      if (filters.sucursal       && p.sucursal_nombre              !== filters.sucursal)        return false;
      if (filters.almacen        && p.almacen_nombre               !== filters.almacen)         return false;
      if (filters.proveedor      && p.proveedor_nombre             !== filters.proveedor)       return false;
      if (filters.tipoMovimiento && p.tipo_movimiento_descripcion  !== filters.tipoMovimiento)  return false;
      if (filters.estado         && p.estado                       !== filters.estado)          return false;
      if (filters.docRef1 && p.tipo_doc_ref1_descripcion !== filters.docRef1) return false;
      if (filters.numRef1 && !(p.num_doc_ref1 || '').toLowerCase().includes(filters.numRef1.toLowerCase())) return false;
      if (!filters.sinFecha) {
        const fecha = (p.fecha_operacion || '').slice(0, 10);
        if (filters.fechaInicio && fecha < filters.fechaInicio) return false;
        if (filters.fechaFin    && fecha > filters.fechaFin)    return false;
      }
      return true;
    });
  }, [partes, filters]);

  const totalPages  = Math.max(1, Math.ceil(filtered.length / (pageSize || filtered.length)));
  const currentPage = Math.min(page, totalPages);
  const paginated   = pageSize === 0
    ? filtered
    : filtered.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  const limpiarFiltros = () => { setFilters(EMPTY_FILTERS); setPage(1); };

  /* ── Ver PS ───────────────────────────────────────────────── */
  const abrirVer = async (ps) => {
    setVerTarget(ps);
    setVerData(null);
    setVerLoading(true);
    try {
      const [detRes, trazRes] = await Promise.all([
        parteSalidaService.getById(ps.id_parte_salida),
        parteSalidaService.getTrazabilidad(ps.id_parte_salida),
      ]);
      setVerData({ cabecera: detRes.data, trazabilidad: trazRes.data });
    } catch (err) {
      setToast({ type: 'error', message: getErrorDetail(err, 'No se pudo cargar el parte.') });
      setVerTarget(null);
    } finally { setVerLoading(false); }
  };
  const cerrarVer = () => { setVerTarget(null); setVerData(null); };

  /* ── Editar PS ────────────────────────────────────────────── */
  const abrirEditar = async (ps) => {
    setEditarTarget(ps);
    setEditarForm(null);
    setEditarLoading(true);
    try {
      const res = await parteSalidaService.getById(ps.id_parte_salida);
      const d = res.data || {};
      setEditarForm({
        derivado: !!d.derivado,
        fecha_operacion:    (d.fecha_operacion || '').slice(0, 10),
        id_almacen:         d.id_almacen || '',
        id_sucursal:        d.id_sucursal || '',
        id_tipo_movimiento: d.id_tipo_movimiento || '',
        ruc_proveedor:      d.ruc_proveedor || '',
        id_proveedor:       d.id_proveedor || '',
        num_doc_ref1:       d.num_doc_ref1 || '',
        num_doc_ref2:       d.num_doc_ref2 || '',
        observacion:        d.observacion || '',
        id_centro_costo:    d.id_centro_costo || '',
        id_solicitante:     d.id_solicitante || '',
        _numero:            d.numero,
        _almacen_nombre:    d.almacen_nombre,
        _proveedor_nombre:  d.proveedor_nombre,
        _tipo_movimiento:   d.tipo_movimiento_descripcion,
      });
    } catch (err) {
      setToast({ type: 'error', message: getErrorDetail(err, 'No se pudo cargar el parte.') });
      setEditarTarget(null);
    } finally { setEditarLoading(false); }
  };
  const cerrarEditar = () => { setEditarTarget(null); setEditarForm(null); };

  const guardarEdicion = async () => {
    if (!editarTarget || !editarForm) return;
    if (!editarForm.fecha_operacion) {
      setToast({ type: 'error', message: 'La fecha es obligatoria.' }); return;
    }
    setEditarSaving(true);
    try {
      const payload = editarForm.derivado
        ? { fecha_operacion: editarForm.fecha_operacion }
        : {
            fecha_operacion: editarForm.fecha_operacion,
            observacion:     editarForm.observacion,
            num_doc_ref1:    editarForm.num_doc_ref1 || null,
            num_doc_ref2:    editarForm.num_doc_ref2 || null,
          };
      await parteSalidaService.update(editarTarget.id_parte_salida, payload);
      setToast({ type: 'success', message: 'Parte de salida actualizado.' });
      cerrarEditar();
      fetchPartes();
    } catch (err) {
      setToast({ type: 'error', message: getErrorDetail(err, 'Error al guardar.') });
    } finally { setEditarSaving(false); }
  };

  const handleEliminar = (ps) => {
    setConfirm({
      message: `¿Eliminar el Parte de Salida N° ${ps.numero}?`,
      onConfirm: async () => {
        setConfirm(null);
        try {
          await parteSalidaService.delete(ps.id_parte_salida);
          setToast({ type: 'success', message: `Parte N° ${ps.numero} eliminado.` });
          setPartes(prev => prev.filter(p => p.id_parte_salida !== ps.id_parte_salida));
        } catch (e) {
          setToast({ type: 'error', message: getErrorDetail(e) });
        }
      },
    });
  };

  const activeFilters = Object.entries(filters).filter(([k, v]) => {
    if (k === 'sinFecha')    return v === true;
    if (k === 'fechaInicio') return v !== weekAgo();
    if (k === 'fechaFin')    return v !== today();
    return !!v;
  }).length;

  return (
    <Layout title="Partes de Salida" subtitle="Almacén">
      {toast   && <Toast type={toast.type} message={toast.message} onClose={() => setToast(null)} />}
      {confirm && (
        <Confirm
          message={confirm.message}
          onConfirm={confirm.onConfirm}
          onCancel={() => setConfirm(null)}
        />
      )}
      {imprimirTarget && (
        <ImprimirParteModal
          tipo="salida"
          parte={imprimirTarget}
          trabajadores={trabajadores}
          onClose={() => setImprimirTarget(null)}
        />
      )}

      {/* ── Encabezado ── */}
      <div className="pel-header">
        <h2 className="pel-title">Listado de Partes de Salida</h2>
        <div className="pel-header-actions">
          <button className="btn btn-secondary btn-sm" onClick={fetchPartes}>↺ Actualizar</button>
          <button className="btn btn-primary btn-sm" onClick={() => navigate('/almacen/parte-salida/nuevo')}>
            + Nuevo Parte
          </button>
        </div>
      </div>

      {/* ── Panel de filtros ── */}
      <div className="pel-filters">
        <div className="pel-filters-grid">

          <div className="pel-filter-field">
            <label>Sucursal</label>
            <AutoComplete
              value={filters.sucursal}
              options={opsSucursal}
              idField="id" displayField="nombre" selectedField="nombre"
              placeholder="Todas"
              onChange={val => setF('sucursal', val || '')}
            />
          </div>

          <div className="pel-filter-field">
            <label>Almacén</label>
            <AutoComplete
              value={filters.almacen}
              options={opsAlmacen}
              idField="id" displayField="nombre" selectedField="nombre"
              placeholder="Todos"
              onChange={val => setF('almacen', val || '')}
            />
          </div>

          <div className="pel-filter-field">
            <label>Proveedor / Cliente</label>
            <AutoComplete
              value={filters.proveedor}
              options={opsProveedor}
              idField="id"
              displayField={(o) => o.ruc ? `${o.ruc} — ${o.nombre}` : o.nombre}
              selectedField="nombre"
              placeholder="Todos"
              onChange={val => setF('proveedor', val || '')}
            />
          </div>

          <div className="pel-filter-field">
            <label>Tipo de Movimiento</label>
            <AutoComplete
              value={filters.tipoMovimiento}
              options={opsTipoMov}
              idField="id" displayField="nombre" selectedField="nombre"
              placeholder="Todos"
              onChange={val => setF('tipoMovimiento', val || '')}
            />
          </div>

          <div className="pel-filter-field">
            <label>Fecha desde</label>
            <input
              type="date" className="pel-date-input"
              value={filters.fechaInicio} disabled={filters.sinFecha}
              onChange={e => setF('fechaInicio', e.target.value)}
            />
          </div>

          <div className="pel-filter-field">
            <label>Fecha hasta</label>
            <div className="pel-fecha-row">
              <input
                type="date" className="pel-date-input"
                value={filters.fechaFin} disabled={filters.sinFecha}
                onChange={e => setF('fechaFin', e.target.value)}
              />
              <button
                className={`pel-btn-todo ${filters.sinFecha ? 'active' : ''}`}
                title={filters.sinFecha ? 'Aplicar rango de fechas' : 'Ver todos sin filtro de fecha'}
                onClick={() => setF('sinFecha', !filters.sinFecha)}
              >
                {filters.sinFecha ? 'Con fecha' : 'Ver todo'}
              </button>
            </div>
          </div>

          <div className="pel-filter-field">
            <label>Doc. Referencia 1</label>
            <AutoComplete
              value={filters.docRef1}
              options={opsDocRef1}
              idField="id" displayField="nombre" selectedField="nombre"
              placeholder="Tipo de documento..."
              onChange={val => setF('docRef1', val || '')}
            />
          </div>

          <div className="pel-filter-field">
            <label>N° Referencia 1</label>
            <input
              type="text" className="pel-text-input"
              placeholder="Número..."
              value={filters.numRef1}
              onChange={e => setF('numRef1', e.target.value)}
            />
          </div>

          <div className="pel-filter-field">
            <label>Estado</label>
            <select
              className="pel-select"
              value={filters.estado}
              onChange={e => setF('estado', e.target.value)}
            >
              <option value="">Todos</option>
              {ESTADOS.map(e => <option key={e} value={e}>{e}</option>)}
            </select>
          </div>

          {activeFilters > 0 && (
            <div className="pel-filter-field pel-filter-clear">
              <label>&nbsp;</label>
              <button className="btn btn-secondary btn-sm" onClick={limpiarFiltros}>
                Limpiar filtros ({activeFilters})
              </button>
            </div>
          )}

        </div>
      </div>

      {/* ── Tabla ── */}
      <div className="pel-table-wrap">
        {loading ? (
          <div className="pel-loading">Cargando partes de salida...</div>
        ) : filtered.length === 0 ? (
          <div className="pel-empty">No hay partes de salida que coincidan con los filtros.</div>
        ) : (
          <table className="pel-table">
            <thead>
              <tr>
                <th>Sucursal</th>
                <th>Almacén</th>
                <th>Proveedor / Cliente</th>
                <th className="center">Fecha Op.</th>
                <th>Tipo Movimiento</th>
                <th>Doc. Referencia</th>
                <th className="center">Estado</th>
                <th className="center" style={{ width: 120 }}>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {paginated.map(ps => {
                const est = ESTADO_CFG[ps.estado] || {};
                const docRef = ps.tipo_doc_ref1_descripcion
                  ? `${ps.tipo_doc_ref1_descripcion}${ps.num_doc_ref1 ? ' · ' + ps.num_doc_ref1 : ''}`
                  : ps.num_doc_ref1 || '—';
                return (
                  <tr key={ps.id_parte_salida}>
                    <td>{ps.sucursal_nombre || '—'}</td>
                    <td>{ps.almacen_nombre || '—'}</td>
                    <td>
                      {ps.ruc_proveedor && <div className="pel-ruc">{ps.ruc_proveedor}</div>}
                      <div className="pel-razon">{ps.proveedor_nombre || '—'}</div>
                    </td>
                    <td className="center">{fmtFecha(ps.fecha_operacion)}</td>
                    <td>{ps.tipo_movimiento_descripcion || '—'}</td>
                    <td className="pel-docref">{docRef}</td>
                    <td className="center">
                      <span className="pel-badge" style={{
                        color: est.color, background: est.bg, border: `1px solid ${est.border || 'transparent'}`
                      }}>
                        {ps.estado || '—'}
                      </span>
                    </td>
                    <td className="center">
                      <div className="pel-actions">
                        <button className="pel-btn-action" title="Ver detalle"
                          onClick={() => abrirVer(ps)}>
                          <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                        </button>
                        <button className="pel-btn-action"
                          title="Editar"
                          onClick={() => abrirEditar(ps)}>
                          <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                        </button>
                        <button className="pel-btn-action pel-btn-danger"
                          title="Eliminar"
                          onClick={() => handleEliminar(ps)}>
                          <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>
                        </button>
                        <button className="pel-btn-action" title="Imprimir"
                          onClick={async () => {
                            try {
                              const r = await parteSalidaService.getById(ps.id_parte_salida);
                              setImprimirTarget(r.data);
                            } catch {
                              setToast({ type: 'error', message: 'Error al cargar el parte.' });
                            }
                          }}>
                          <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="6 9 6 2 18 2 18 9"/><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/><rect x="6" y="14" width="12" height="8"/></svg>
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* ── Paginación ── */}
      {!loading && filtered.length > 0 && (
        <div className="pel-pagination">
          <div className="pel-page-info">
            <span>Filas por página:</span>
            <select value={pageSize} onChange={e => { setPageSize(Number(e.target.value)); setPage(1); }}>
              {PAGE_SIZES.map(s => <option key={s} value={s}>{s}</option>)}
              <option value={0}>Todos</option>
            </select>
            <span style={{ marginLeft: 12 }}>
              {pageSize === 0 ? filtered.length : Math.min((currentPage - 1) * pageSize + 1, filtered.length)}–{pageSize === 0 ? filtered.length : Math.min(currentPage * pageSize, filtered.length)} de {filtered.length}
            </span>
          </div>
          {pageSize !== 0 && totalPages > 1 && (
            <div className="pel-page-nav">
              <button disabled={currentPage === 1} onClick={() => setPage(1)}>«</button>
              <button disabled={currentPage === 1} onClick={() => setPage(p => p - 1)}>‹</button>
              {Array.from({ length: totalPages }, (_, i) => i + 1)
                .filter(p => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 1)
                .reduce((acc, p, i, arr) => {
                  if (i > 0 && p - arr[i - 1] > 1) acc.push('...');
                  acc.push(p);
                  return acc;
                }, [])
                .map((p, i) => p === '...'
                  ? <span key={`e${i}`} className="pel-page-ellipsis">…</span>
                  : <button key={p} className={currentPage === p ? 'active' : ''} onClick={() => setPage(p)}>{p}</button>
                )
              }
              <button disabled={currentPage === totalPages} onClick={() => setPage(p => p + 1)}>›</button>
              <button disabled={currentPage === totalPages} onClick={() => setPage(totalPages)}>»</button>
            </div>
          )}
        </div>
      )}

      {/* ── Modal Ver PS ── */}
      {verTarget && (
        <div className="modal-overlay">
          <div className="modal modal-xl" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <div>
                <h2>Parte de Salida — PS-{verTarget.numero}</h2>
                <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 2 }}>
                  {verData?.cabecera?.almacen_nombre || verTarget.almacen_nombre || '—'}
                </div>
              </div>
              <button className="modal-close" onClick={cerrarVer}>×</button>
            </div>
            <div className="modal-body" style={{ padding: 20 }}>
              {verLoading || !verData ? (
                <div style={{ padding: 40, textAlign: 'center', color: '#64748b' }}>Cargando...</div>
              ) : (
                <VerPsContenido data={verData} />
              )}
            </div>
            <div style={{ padding: '12px 20px', borderTop: '1px solid rgba(255,255,255,0.07)', display: 'flex', justifyContent: 'flex-end' }}>
              <button className="btn btn-secondary" onClick={cerrarVer}>Cerrar</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Modal Editar PS ── */}
      {editarTarget && (
        <div className="modal-overlay">
          <div className="modal" style={{ maxWidth: 640 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <div>
                <h2>Editar Parte de Salida — PS-{editarTarget.numero}</h2>
                {editarForm?.derivado && (
                  <div style={{ fontSize: 11.5, color: '#fb923c', marginTop: 3 }}>
                    ⚠ Este parte proviene de un parte de entrada; solo puede modificarse la fecha.
                  </div>
                )}
              </div>
              <button className="modal-close" onClick={cerrarEditar}>×</button>
            </div>
            <div className="modal-body" style={{ padding: 20 }}>
              {editarLoading || !editarForm ? (
                <div style={{ padding: 40, textAlign: 'center', color: '#64748b' }}>Cargando...</div>
              ) : (
                <EditarPsForm form={editarForm} onChange={setEditarForm} />
              )}
            </div>
            <div style={{ padding: '12px 20px', borderTop: '1px solid rgba(255,255,255,0.07)', display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button className="btn btn-secondary" onClick={cerrarEditar} disabled={editarSaving}>Cancelar</button>
              <button className="btn btn-primary" onClick={guardarEdicion} disabled={editarSaving || editarLoading}>
                {editarSaving ? 'Guardando...' : 'Guardar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}

/* ── Modal Ver: pestañas Información / Comprobantes origen ── */
const VerPsContenido = ({ data }) => {
  const c = data.cabecera || {};
  const t = data.trazabilidad || {};
  const fmtFchS = (f) => (f || '').slice(0, 10) || '—';
  const [tab, setTab] = React.useState('info');

  const countOrigen = (t.pes_origen || []).length;

  const tabs = [
    { key: 'info',   label: 'Información',         color: '#60a5fa', count: null },
    { key: 'origen', label: 'Comprobantes origen', color: '#a78bfa', count: countOrigen },
  ];

  const Field = ({ label, value }) => (
    <div style={{ flex: '1 1 200px', minWidth: 0 }}>
      <div style={{ fontSize: 10, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 2 }}>{label}</div>
      <div style={{ fontSize: 13, color: '#e2e8f0', fontWeight: 500 }}>{value || '—'}</div>
    </div>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div style={{ display: 'flex', gap: 4, borderBottom: '1px solid rgba(255,255,255,0.08)', overflowX: 'auto' }}>
        {tabs.map(tb => {
          const active = tab === tb.key;
          return (
            <button key={tb.key} type="button" onClick={() => setTab(tb.key)}
              style={{
                padding: '10px 18px', background: active ? 'rgba(15,27,45,0.8)' : 'transparent',
                border: 'none', borderBottom: active ? `2px solid ${tb.color}` : '2px solid transparent',
                color: active ? '#e2e8f0' : '#94a3b8', fontSize: 13, fontWeight: active ? 600 : 500,
                cursor: 'pointer', whiteSpace: 'nowrap', transition: 'all 0.15s ease',
                display: 'flex', alignItems: 'center', gap: 8,
              }}>
              <span style={{ display: 'inline-block', width: 7, height: 7, borderRadius: '50%', background: tb.color }} />
              {tb.label}
              {tb.count !== null && (
                <span style={{ padding: '1px 7px', fontSize: 10, borderRadius: 10, background: tb.count > 0 ? tb.color : 'rgba(148,163,184,0.15)', color: tb.count > 0 ? '#0f1b2d' : '#64748b', fontWeight: 700 }}>{tb.count}</span>
              )}
            </button>
          );
        })}
      </div>

      {tab === 'info' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div style={{ padding: 14, borderRadius: 10, background: 'rgba(15,27,45,0.5)', border: '1px solid rgba(255,255,255,0.06)' }}>
            <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap' }}>
              <Field label="Número" value={`PS-${c.numero}`} />
              <Field label="Fecha de salida" value={fmtFchS(c.fecha_operacion)} />
              <Field label="Almacén" value={c.almacen_nombre} />
              <Field label="Sucursal" value={c.sucursal_nombre} />
              <Field label="Tipo Movimiento" value={c.tipo_movimiento_descripcion} />
              <Field label="Estado" value={c.estado} />
            </div>
            <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap', marginTop: 14 }}>
              <Field label="Proveedor/Cliente" value={c.proveedor_nombre} />
              <Field label="RUC" value={c.ruc_proveedor} />
              <Field label="Solicitante" value={c.solicitante_nombre} />
              <Field label="Centro de Costo" value={c.centro_costo_descripcion} />
            </div>
            {(c.num_doc_ref1 || c.num_doc_ref2) && (
              <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap', marginTop: 14 }}>
                {c.num_doc_ref1 && <Field label={c.tipo_doc_ref1_descripcion || 'Doc. Ref. 1'} value={c.num_doc_ref1} />}
                {c.num_doc_ref2 && <Field label={c.tipo_doc_ref2_descripcion || 'Doc. Ref. 2'} value={c.num_doc_ref2} />}
              </div>
            )}
            {c.observacion && (
              <div style={{ marginTop: 14 }}>
                <Field label="Observación" value={c.observacion} />
              </div>
            )}
          </div>

          <div>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#e2e8f0', textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: 8 }}>
              Ítems ({(c.detalle || []).length})
            </div>
            <div style={{ borderRadius: 10, overflow: 'hidden', border: '1px solid rgba(255,255,255,0.06)' }}>
              <table className="data-table" style={{ width: '100%', margin: 0, fontSize: 12 }}>
                <thead>
                  <tr>
                    <th style={{ width: 100 }}>Código</th>
                    <th>Descripción</th>
                    <th style={{ width: 80 }}>Unidad</th>
                    <th style={{ width: 80, textAlign: 'right' }}>Cantidad</th>
                    <th>Observación</th>
                  </tr>
                </thead>
                <tbody>
                  {(c.detalle || []).length === 0 ? (
                    <tr><td colSpan={5} style={{ textAlign: 'center', color: '#64748b', padding: 18 }}>Sin ítems</td></tr>
                  ) : (c.detalle || []).map((d, i) => (
                    <tr key={i}>
                      <td style={{ fontFamily: 'monospace', fontSize: 11 }}>{d.articulo_codigo || '—'}</td>
                      <td>{d.articulo_descripcion || '—'}</td>
                      <td>{d.unidad_descripcion || d.unidad_codigo || '—'}</td>
                      <td style={{ textAlign: 'right', fontWeight: 600 }}>{(parseFloat(d.cantidad) || 0).toLocaleString('es-PE', { maximumFractionDigits: 4 })}</td>
                      <td style={{ color: '#94a3b8' }}>{d.observacion || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {tab === 'origen' && (
        countOrigen === 0 ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#64748b', fontStyle: 'italic' }}>
            Este parte de salida no proviene de un parte de entrada previo.
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 12 }}>
            <TrazabilidadBlock
              title="Parte de Entrada origen" color="#60a5fa"
              items={t.pes_origen || []}
              render={p => `PE-${p.numero} · ${fmtFchS(p.fecha_operacion)}${p.almacen_nombre ? ' · ' + p.almacen_nombre : ''}`}
              estado={p => p.estado}
            />
          </div>
        )
      )}
    </div>
  );
};

const TrazabilidadBlock = ({ title, color, items, render, estado }) => (
  <div style={{
    padding: 12, borderRadius: 10,
    background: 'rgba(15,27,45,0.5)', border: `1px solid ${color}33`, borderLeft: `3px solid ${color}`,
  }}>
    <div style={{ fontSize: 10.5, fontWeight: 700, color, textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: 8 }}>
      {title} ({items.length})
    </div>
    {items.length === 0 ? (
      <div style={{ fontSize: 11.5, color: '#64748b', fontStyle: 'italic' }}>Sin registros</div>
    ) : (
      <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 6 }}>
        {items.map((it, i) => (
          <li key={i} style={{ fontSize: 12, color: '#e2e8f0', display: 'flex', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
            <span>{render(it)}</span>
            {estado(it) && (
              <span style={{ fontSize: 10, fontWeight: 700, color, padding: '1px 6px', background: `${color}22`, borderRadius: 4, border: `1px solid ${color}44` }}>
                {estado(it)}
              </span>
            )}
          </li>
        ))}
      </ul>
    )}
  </div>
);

/* ── Modal Editar ── */
const EditarPsForm = ({ form, onChange }) => {
  const set = (key, val) => onChange({ ...form, [key]: val });
  const labelS = { fontSize: 10.5, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.6, fontWeight: 600, marginBottom: 4, display: 'block' };
  const inpS   = {
    width: '100%', padding: '8px 10px', borderRadius: 8, fontSize: 13,
    background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.09)',
    color: '#e2e8f0', outline: 'none', boxSizing: 'border-box',
  };
  const inpRO  = { ...inpS, background: 'rgba(255,255,255,0.02)', color: '#64748b' };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <div>
          <label style={labelS}>Número</label>
          <input style={inpRO} value={`PS-${form._numero || ''}`} readOnly />
        </div>
        <div>
          <label style={labelS}>Fecha de salida *</label>
          <input type="date" style={inpS} value={form.fecha_operacion || ''} onChange={e => set('fecha_operacion', e.target.value)} />
        </div>
        <div>
          <label style={labelS}>Almacén</label>
          <input style={inpRO} value={form._almacen_nombre || '—'} readOnly />
        </div>
        <div>
          <label style={labelS}>Tipo de Movimiento</label>
          <input style={inpRO} value={form._tipo_movimiento || '—'} readOnly />
        </div>
        <div>
          <label style={labelS}>Proveedor/Cliente</label>
          <input style={inpRO} value={form._proveedor_nombre || '—'} readOnly />
        </div>
        <div>
          <label style={labelS}>Doc. Ref. 1</label>
          <input style={form.derivado ? inpRO : inpS} value={form.num_doc_ref1 || ''} readOnly={!!form.derivado}
                 onChange={e => set('num_doc_ref1', e.target.value)} placeholder="—" />
        </div>
        <div>
          <label style={labelS}>Doc. Ref. 2</label>
          <input style={form.derivado ? inpRO : inpS} value={form.num_doc_ref2 || ''} readOnly={!!form.derivado}
                 onChange={e => set('num_doc_ref2', e.target.value)} placeholder="—" />
        </div>
      </div>
      <div>
        <label style={labelS}>Observación</label>
        <textarea
          rows={3}
          style={{ ...(form.derivado ? inpRO : inpS), resize: 'vertical' }}
          value={form.observacion || ''}
          readOnly={!!form.derivado}
          onChange={e => set('observacion', e.target.value)}
          placeholder={form.derivado ? 'No editable en parte derivado' : 'Observación del parte...'}
        />
      </div>
    </div>
  );
};
